import { useEffect, useRef, useState } from 'react'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

const easeOut = (t: number) => 1 - (1 - t) ** 3

/** Counts from 0 up to `target` when the figure appears, and from the figure shown to the new
 *  `target` when it changes, as when the year switches under it; the value itself when `enabled`
 *  is off. */
export const useCountUp = (target: number | null, enabled: boolean, durationMs = 700) => {
  const animate = enabled && target !== null && !prefersReducedMotion()
  const [value, setValue] = useState(animate ? 0 : target)
  /* The figure on screen, where the next count starts. */
  const shownRef = useRef<number | null>(animate ? 0 : target)

  useEffect(() => {
    if (!animate || target === null) {
      shownRef.current = target
      setValue(target)
      return
    }

    const from = shownRef.current ?? 0
    let frame = 0
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      const next = from + (target - from) * easeOut(t)

      shownRef.current = next
      setValue(next)

      if (t < 1) {
        frame = requestAnimationFrame(tick)
      }
    }

    frame = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(frame)
  }, [animate, durationMs, target])

  return value
}
